<script>
/**
 * Aviso de cookies del dashboard.
 *
 * Réplica del CMP de la web (www.lixsa.ai): mismo contrato para que un usuario
 * no se encuentre dos criterios distintos entre la web y la app.
 *   - clave de localStorage: lixsa_cookie_consent_v1
 *   - categorías: necessary (fija) + analytics, personalization, marketing
 *   - al decidir: window.lixsaConsentStatus, evento `lixsaConsentUpdate`
 *     y push a dataLayer con `lixsa_consent_update`
 *   - scripts diferidos: <script type="text/plain" data-cookiecategory="...">
 *
 * Ojo: la web vive en www.lixsa.ai y la app en app.lixsa.ai, así que el
 * consentimiento NO viaja entre las dos (localStorage es por origen). El
 * formato es el mismo por coherencia y para poder unificar más adelante.
 */
const STORAGE_KEY = 'lixsa_cookie_consent_v1';
const CATEGORIES = ['analytics', 'personalization', 'marketing'];

export default {
    name: 'CookieConsent',
    data() {
        return {
            visible: false,
            panelOpen: false,
            choices: { analytics: false, personalization: false, marketing: false },
        };
    },
    created() {
        const saved = this.getSavedConsent();
        if (saved) {
            this.applyConsent(saved);
        } else {
            this.visible = true;
        }
        // Igual que en la web: cualquier elemento con esta clase reabre el panel
        document.addEventListener('click', this.handleReopenTrigger);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleReopenTrigger);
        document.body.style.overflow = '';
    },
    methods: {
        getSavedConsent() {
            try {
                const raw = localStorage.getItem(STORAGE_KEY);
                return raw ? JSON.parse(raw) : null;
            } catch (err) {
                console.warn('No se pudo leer el consentimiento de cookies:', err);
                return null;
            }
        },

        saveConsent(consent) {
            const payload = { ...consent, timestamp: new Date().toISOString(), version: '1.0' };
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
            } catch (err) {
                console.warn('No se pudo guardar el consentimiento de cookies:', err);
            }
            this.applyConsent(consent);
        },

        /** Activa los scripts diferidos de las categorías aceptadas y avisa al resto de la app. */
        applyConsent(consent) {
            document.querySelectorAll('script[type="text/plain"][data-cookiecategory]').forEach((node) => {
                const category = node.getAttribute('data-cookiecategory');
                if (consent[category] !== true) return;
                const script = document.createElement('script');
                if (node.src) {
                    script.src = node.src;
                } else {
                    script.innerHTML = node.innerHTML;
                }
                Array.from(node.attributes).forEach((attr) => {
                    if (attr.name !== 'type' && attr.name !== 'data-cookiecategory') {
                        script.setAttribute(attr.name, attr.value);
                    }
                });
                script.type = 'text/javascript';
                node.parentNode.replaceChild(script, node);
            });

            window.lixsaConsentStatus = consent;
            window.dispatchEvent(new CustomEvent('lixsaConsentUpdate', { detail: consent }));
            if (window.dataLayer) {
                window.dataLayer.push({ event: 'lixsa_consent_update', consent_status: consent });
            }
        },

        acceptAll() {
            this.saveConsent({ necessary: true, analytics: true, personalization: true, marketing: true });
            this.close();
        },

        rejectAll() {
            this.saveConsent({ necessary: true, analytics: false, personalization: false, marketing: false });
            this.close();
        },

        saveSelection() {
            const consent = { necessary: true };
            let anyAccepted = false;
            CATEGORIES.forEach((category) => {
                consent[category] = this.choices[category];
                if (this.choices[category]) anyAccepted = true;
            });
            // Sin ninguna marcada, equivale a rechazar todo
            if (!anyAccepted) {
                this.rejectAll();
                return;
            }
            this.saveConsent(consent);
            this.close();
        },

        openPanel() {
            const saved = this.getSavedConsent() || {};
            CATEGORIES.forEach((c) => {
                this.choices[c] = saved[c] === true;
            });
            this.visible = false;
            this.panelOpen = true;
            document.body.style.overflow = 'hidden';
        },

        closePanel() {
            this.panelOpen = false;
            document.body.style.overflow = '';
            // Si aún no ha decidido, el banner vuelve
            if (!this.getSavedConsent()) this.visible = true;
        },

        close() {
            this.visible = false;
            this.panelOpen = false;
            document.body.style.overflow = '';
        },

        handleReopenTrigger(event) {
            const el = event.target;
            if (el && el.closest && el.closest('.klx-open-cookie-panel, #klx-open-cookie-panel')) {
                event.preventDefault();
                this.openPanel();
            }
        },

        policyUrl() {
            const lang = (this.$i18n?.locale || 'en').toLowerCase();
            return lang.startsWith('es') ? 'https://www.lixsa.ai/es/cookies/' : 'https://www.lixsa.ai/en/cookies/';
        },
    },
};
</script>

<template>
    <div>
        <!-- Capa 1: banner -->
        <div
            v-if="visible"
            class="cc-banner"
            role="dialog"
            aria-modal="false"
            aria-labelledby="cc-banner-title"
        >
            <div class="cc-banner__content">
                <div class="cc-banner__text">
                    <h3 id="cc-banner-title" class="cc-banner__title">{{ $t('cookies.title') }}</h3>
                    <p class="cc-banner__body">
                        {{ $t('cookies.bannerText') }}
                        <a :href="policyUrl()" target="_blank" rel="noopener noreferrer" class="cc-link">
                            {{ $t('cookies.policyLink') }}
                        </a>
                    </p>
                </div>
                <div class="cc-banner__actions">
                    <button type="button" class="cc-btn cc-btn--ghost" @click="rejectAll">
                        {{ $t('cookies.rejectAll') }}
                    </button>
                    <button type="button" class="cc-btn cc-btn--ghost" @click="openPanel">
                        {{ $t('cookies.configure') }}
                    </button>
                    <button type="button" class="cc-btn cc-btn--primary" @click="acceptAll">
                        {{ $t('cookies.acceptAll') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Capa 2: panel de configuración -->
        <div v-if="panelOpen" class="cc-overlay" @click.self="closePanel">
            <div class="cc-panel" role="dialog" aria-modal="true" aria-labelledby="cc-panel-title">
                <header class="cc-panel__header">
                    <h3 id="cc-panel-title" class="cc-panel__title">{{ $t('cookies.panelTitle') }}</h3>
                    <button
                        type="button"
                        class="cc-panel__close"
                        :aria-label="$t('cookies.close')"
                        @click="closePanel"
                    >
                        <i class="material-symbols-outlined">close</i>
                    </button>
                </header>

                <div class="cc-panel__body">
                    <p class="cc-panel__intro">{{ $t('cookies.panelIntro') }}</p>

                    <div class="cc-cat">
                        <div class="cc-cat__head">
                            <h4 class="cc-cat__title">{{ $t('cookies.necessary.title') }}</h4>
                            <span class="cc-badge">{{ $t('cookies.alwaysOn') }}</span>
                        </div>
                        <p class="cc-cat__desc">{{ $t('cookies.necessary.desc') }}</p>
                    </div>

                    <div v-for="cat in ['analytics', 'personalization', 'marketing']" :key="cat" class="cc-cat">
                        <div class="cc-cat__head">
                            <h4 class="cc-cat__title">{{ $t('cookies.' + cat + '.title') }}</h4>
                            <label class="cc-switch">
                                <input type="checkbox" v-model="choices[cat]" />
                                <span class="cc-switch__track"></span>
                            </label>
                        </div>
                        <p class="cc-cat__desc">{{ $t('cookies.' + cat + '.desc') }}</p>
                    </div>
                </div>

                <footer class="cc-panel__footer">
                    <button type="button" class="cc-btn cc-btn--ghost" @click="rejectAll">
                        {{ $t('cookies.rejectAll') }}
                    </button>
                    <button type="button" class="cc-btn cc-btn--outline" @click="saveSelection">
                        {{ $t('cookies.saveSelection') }}
                    </button>
                    <button type="button" class="cc-btn cc-btn--primary" @click="acceptAll">
                        {{ $t('cookies.acceptAll') }}
                    </button>
                </footer>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.cc-banner {
    position: fixed;
    left: 16px;
    right: 16px;
    bottom: 16px;
    z-index: 9998;
    background: #fff;
    border: 1px solid var(--lx-slate-200);
    border-radius: 16px;
    box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.12), 0 4px 6px -4px rgba(15, 23, 42, 0.1);
    padding: 20px 24px;
    font-family: 'Inter', system-ui, sans-serif;
    animation: cc-in 0.25s cubic-bezier(0, 0, 0.2, 1);

    @media (max-width: 640px) {
        left: 8px;
        right: 8px;
        bottom: 8px;
        padding: 16px;
        max-height: 80vh;
        overflow-y: auto;
    }
}

.cc-banner__content {
    max-width: 1180px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    gap: 24px;

    @media (max-width: 900px) {
        flex-direction: column;
        align-items: stretch;
        gap: 14px;
    }
}

.cc-banner__text {
    flex: 1;
    min-width: 0;
}

.cc-banner__title {
    margin: 0 0 4px;
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 17px;
    font-weight: 600;
    color: var(--lx-slate-900);
}

.cc-banner__body {
    margin: 0;
    font-size: 13.5px;
    line-height: 1.55;
    color: var(--lx-slate-600);
}

.cc-banner__actions {
    display: flex;
    gap: 10px;
    flex: 0 0 auto;

    @media (max-width: 900px) {
        flex-wrap: wrap;
    }

    @media (max-width: 640px) {
        flex-direction: column-reverse;
    }
}

.cc-link {
    color: var(--lx-primary);
    font-weight: 500;
    text-decoration: underline;
}

.cc-btn {
    border: none;
    border-radius: 12px;
    padding: 12px 20px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.18s, border-color 0.18s, transform 0.12s;

    &:active {
        transform: scale(0.985);
    }

    &--primary {
        background: var(--lx-primary);
        color: #fff;

        &:hover {
            background: var(--lx-primary-strong);
        }
    }

    &--ghost {
        background: transparent;
        color: var(--lx-slate-600);

        &:hover {
            background: var(--lx-slate-100);
        }
    }

    &--outline {
        background: #fff;
        color: var(--lx-primary);
        border: 1px solid var(--lx-slate-300);

        &:hover {
            border-color: var(--lx-primary);
        }
    }

    @media (max-width: 640px) {
        width: 100%;
        padding: 14px 20px;
    }
}

.cc-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(15, 23, 42, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: cc-fade 0.2s ease;

    @media (max-width: 640px) {
        padding: 0;
        align-items: flex-end;
    }
}

.cc-panel {
    background: #fff;
    border-radius: 18px;
    width: 100%;
    max-width: 640px;
    max-height: 86vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: 'Inter', system-ui, sans-serif;
    box-shadow: 0 20px 25px -5px rgba(15, 23, 42, 0.2);

    @media (max-width: 640px) {
        max-width: none;
        max-height: 92vh;
        border-radius: 20px 20px 0 0;
        padding-bottom: env(safe-area-inset-bottom, 0);
    }
}

.cc-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 20px 24px;
    border-bottom: 1px solid var(--lx-slate-100);
}

.cc-panel__title {
    margin: 0;
    font-family: 'Outfit', 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 600;
    color: var(--lx-slate-900);
}

.cc-panel__close {
    border: none;
    background: transparent;
    cursor: pointer;
    color: var(--lx-slate-500);
    border-radius: 10px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: var(--lx-slate-100);
    }
}

.cc-panel__body {
    padding: 20px 24px;
    overflow-y: auto;
    overscroll-behavior: contain;
}

.cc-panel__intro {
    margin: 0 0 18px;
    font-size: 13.5px;
    line-height: 1.6;
    color: var(--lx-slate-500);
}

.cc-cat {
    border: 1px solid var(--lx-slate-200);
    border-radius: 14px;
    padding: 16px;
    margin-bottom: 12px;
}

.cc-cat__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
}

.cc-cat__title {
    margin: 0;
    font-size: 14.5px;
    font-weight: 600;
    color: var(--lx-slate-800);
}

.cc-cat__desc {
    margin: 0;
    font-size: 13px;
    line-height: 1.55;
    color: var(--lx-slate-500);
}

.cc-badge {
    flex: 0 0 auto;
    font-size: 11px;
    font-weight: 600;
    color: var(--lx-primary-deep);
    background: var(--lx-primary-tint);
    border-radius: 999px;
    padding: 4px 10px;
}

/* Interruptor */
.cc-switch {
    flex: 0 0 auto;
    position: relative;
    display: inline-block;
    width: 46px;
    height: 26px;
    cursor: pointer;

    input {
        position: absolute;
        opacity: 0;
        width: 0;
        height: 0;
    }
}

.cc-switch__track {
    position: absolute;
    inset: 0;
    background: var(--lx-slate-300);
    border-radius: 999px;
    transition: background 0.2s;

    &::before {
        content: '';
        position: absolute;
        top: 3px;
        left: 3px;
        width: 20px;
        height: 20px;
        background: #fff;
        border-radius: 50%;
        transition: transform 0.2s;
        box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);
    }
}

.cc-switch input:checked + .cc-switch__track {
    background: var(--lx-primary);

    &::before {
        transform: translateX(20px);
    }
}

.cc-switch input:focus-visible + .cc-switch__track {
    outline: 2px solid var(--lx-primary);
    outline-offset: 2px;
}

.cc-panel__footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 24px;
    border-top: 1px solid var(--lx-slate-100);

    @media (max-width: 640px) {
        flex-direction: column-reverse;
    }
}

@keyframes cc-in {
    from {
        opacity: 0;
        transform: translateY(12px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes cc-fade {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}
</style>
